import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Stack, TextField, InputAdornment, Alert, LinearProgress } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import { useSnackbar } from 'notistack'
import PageTitle from '../../../components/PageTitle'
import { KpiRow, Pill, SectionCard, ActionIconBtn, PrimaryActionIcon } from '../components/common'
import DataTable from '../components/Tables'
import ProposalDetailModal from '../components/ProposalDetailModal'
import { PATHS } from '../constants'
import { useListProposalsQuery, useLazyGetOrderDataTemplateQuery } from '../../../redux/d2cApiSlice'
import { proposalRow, computeKpis } from '../apiMap'

// Deals waiting on order-level data show the CSV-template download inline on this page.
const AWAITING_ORDER_DATA = 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA'

export default function SLDashboard() {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [q, setQ] = useState('')
  const [detail, setDetail] = useState(null)
  const { data, isLoading, isError, error } = useListProposalsQuery({ page: 0, size: 100 })
  const [fetchTemplate, templateState] = useLazyGetOrderDataTemplateQuery()

  // Order-level data CSV template — same blob endpoint used in the detail modal,
  // surfaced here so the SL can grab it from the main page without opening Review.
  const doDownloadTemplate = async () => {
    try {
      const blob = await fetchTemplate().unwrap()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'd2c_order_data_template.csv'
      document.body.appendChild(a); a.click(); a.remove()
      window.URL.revokeObjectURL(url)
    } catch (e) { enqueueSnackbar(e?.message || 'Could not download template.', { variant: 'error' }) }
  }

  const allRows = useMemo(() => {
    const mapped = Array.isArray(data) ? data.map(proposalRow) : []
    return [...mapped].sort((a, b) => new Date(b.raw?.updatedAt || 0) - new Date(a.raw?.updatedAt || 0)) // most recently changed first
  }, [data])
  const kpis = useMemo(() => computeKpis(data, 'sl'), [data])
  const rows = useMemo(() => {
    if (!q.trim()) return allRows
    const t = q.toLowerCase()
    return allRows.filter((r) =>
      [r.brand, r.hubspot, r.cardType, r.status, r.outcome].join(' ').toLowerCase().includes(t),
    )
  }, [q, allRows])

  const columns = [
    { key: 'brand', label: 'Brand', render: (r) => <b>{r.brand}</b> },
    { key: 'hubspot', label: 'HubSpot' },
    { key: 'cardType', label: 'Card Type' },
    { key: 'status', label: 'Status', render: (r) => <Pill label={r.status} styleKey={r.statusKey} /> },
    { key: 'tat', label: 'Turnaround', align: 'right' },
    { key: 'outcome', label: 'Outcome', render: (r) => <Pill label={r.outcome} styleKey={r.outcomeKey} /> },
    {
      key: 'action', label: 'Action', align: 'right', render: (r) => (
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="flex-end">
          {r.apiStatus === AWAITING_ORDER_DATA && (
            <ActionIconBtn title="Order-data template" disabled={templateState.isLoading} onClick={() => doDownloadTemplate()}>
              <FileDownloadOutlinedIcon fontSize="small" />
            </ActionIconBtn>
          )}
          <ActionIconBtn
            title={r.action}
            onClick={() => {
              if (r.apiStatus === 'RETURNED_TO_SL') navigate(PATHS.slCounterReview, { state: { proposalId: r.id, brand: r.brand } })
              else if (r.resumable) navigate(PATHS.newProposal, { state: { resume: r.raw } })
              else setDetail(r)
            }}
          >
            <PrimaryActionIcon action={r.action} />
          </ActionIconBtn>
        </Stack>
      ),
    },
  ]

  return (
    <Box sx={{ px: 3 }}>
      <PageTitle>D2C Pricing Agent · Dashboard</PageTitle>
      <KpiRow items={kpis} />
      {isLoading && <LinearProgress sx={{ my: 1 }} />}
      {isError && <Alert severity="error" sx={{ my: 1 }}>{error?.message || 'Failed to load commercials.'}</Alert>}
      <SectionCard
        title="My Commercials"
        subtitle="Everything in one place — drafts, work in progress, approvals pending and shared proposals. Reuse any past solution as a template."
        action={<Button variant="contained" startIcon={<AddIcon />} sx={{ textTransform: 'none', borderRadius: '6px' }} onClick={() => navigate(PATHS.newProposal)}>New Commercial</Button>}
      >
        <TextField
          size="small" placeholder="Filter commercials…" value={q} onChange={(e) => setQ(e.target.value)}
          sx={{ mb: 1.5, width: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
        />
        <DataTable columns={columns} rows={rows} onRowClick={(r) => setDetail(r)} getRowKey={(r) => r.id} emptyText={isLoading ? 'Loading…' : 'No commercials yet'} />
      </SectionCard>
      <ProposalDetailModal open={!!detail} proposal={detail} onClose={() => setDetail(null)} />
    </Box>
  )
}
